import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { platform } from '../services/platform'

/**
 * Wires the Android hardware back button to in-app navigation.
 *
 * Capacitor's default falls back to the native WebView's `canGoBack()`, which
 * doesn't see SPA (pushState) history — so the hardware back button used to
 * exit the app from every screen. This instead:
 *   - goes back through the app's own history on inner screens, and
 *   - backgrounds the app (like the OS home behaviour) only at a top-level
 *     screen, where there's nowhere further back to go.
 *
 * No-op on web; the plugin is loaded lazily so the PWA bundle stays clean.
 */

/** Screens where "back" should leave the app rather than navigate within it. */
const ROOT_PATHS = new Set<string>([
  '/', '/welcome', '/language', '/login', '/staff/login',
  '/staff/home', '/buyer/home', '/inspector/home'
])

export function useAndroidBackButton (): void {
  const location = useLocation()
  const navigate = useNavigate()

  // Keep the latest path in a ref so the single native listener always reads
  // the current route without being re-registered on every navigation.
  const pathRef = useRef(location.pathname)
  pathRef.current = location.pathname

  useEffect(() => {
    if (!platform.isNative()) return

    let remove: (() => void) | undefined
    let cancelled = false

    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        const handle = await App.addListener('backButton', () => {
          const path = pathRef.current
          if (ROOT_PATHS.has(path)) {
            // At a home/entry screen — background the app instead of walking to
            // a blank history entry.
            void App.minimizeApp()
          } else {
            navigate(-1)
          }
        })
        if (cancelled) { void handle.remove() } else { remove = () => { void handle.remove() } }
      } catch (err) {
        console.warn('[backButton] handler not installed:', err)
      }
    })()

    return () => { cancelled = true; if (remove) remove() }
  }, [navigate])
}
